/**
 * Portfolio Analytics — Phase 2 (full UI)
 *
 * Five tabs: Overview · Income Analysis · Member Health · Room Performance · Settings
 *
 * Gated behind:
 *   1. Professional subscription tier (plan gate, fail-closed while loading)
 *   2. 'portfolio_analytics' beta grant (BetaAccessGate)
 *
 * PADSPLIT DATA WISHLIST — placeholders collected in shared/schema.ts (tagged
 * PADSPLIT_WISHLIST). Ingest when PadSplit exposes the API:
 *   Member: on_time_payment_pct, late_fee_event_count, move_out_reason_code
 *   Room:   days_vacant_per_room, booking_fee_per_move_in, direct_booking_waiver,
 *           application_count
 */

import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Link } from "wouter";
import { apiRequest } from "@/lib/queryClient";
import { parseErrorMessage, parseRateLimitInfo } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import AppLayout from "@/components/app-layout";
import { AiCoachingInsightsPanel } from "@/components/ai-coaching-insights-panel";
import { BetaAccessGate } from "@/components/beta-access-gate";
import { ExplainerVideoButton } from "@/components/explainer-video-button";
import { GuideButton } from "@/components/guide-button";
import { GuideNudgeBanner } from "@/components/guide-nudge-banner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
} from "@/components/ui/form";
import {
  TrendingUp,
  TrendingDown,
  Minus,
  DollarSign,
  Home,
  Users,
  AlertTriangle,
  Settings,
  BarChart3,
  Activity,
  BedDouble,
  Lock,
  Info,
  Sparkles,
  Download,
  RefreshCw,
} from "lucide-react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  Legend,
  PieChart,
  Pie,
} from "recharts";
import { hasFeatureAccess, type AccountType } from "@shared/schema";
import { BookingRequestsOverviewCard } from "@/components/booking-requests-overview-card";
import { AnalyticsSortHeader, useAnalyticsSort, sortAnalyticsRows, memberTierRank, memberTrendRank, roomTrendDelta } from "@/components/analytics-table-sort";

// ─── Types ────────────────────────────────────────────────────────────────────

type EconTier = "green" | "yellow" | "orange" | "red" | "unknown";
type TierCounts = Record<EconTier, number>;

type VarianceData = {
  totalChange: number;
  calendarEffect: number;
  occupancyChange: number;
  pricingChange: number;
  collectionRateChange: number;
  lateFeeIncome: number;
  other: number;
  collectionRateDrop: boolean;
};

type AiSummaryData = {
  available: boolean;
  cached: boolean;
  summary: string | null;
  month: string;
  generatedAt?: string;
  reason?: string;
};

type OverviewData = {
  month: string;
  prevMonth: string;
  hasComparison: boolean;
  notEnoughData: boolean;
  totalRooms: number;
  occupiedRooms: number;
  vacantRooms: number;
  occupancyRate: number;
  activeMembers: number;
  revpar: number | null;
  prevRevpar: number | null;
  revparChange: number | null;
  collectionRate: number | null;
  collectionRateAlert: boolean;
  alertThreshold: number;
  totalCollected: number;
  prevTotalCollected: number;
  dues: number;
  lateFees: number;
  bookingFees: number;
  tierCounts: TierCounts;
  variance: VarianceData | null;
};

type MonthlyIncome = {
  month: string;
  dues: number;
  lateFees: number;
  bookingFees: number;
  serviceFees: number;
  otherIncome: number;
  total: number;
};

type PropertyIncome = {
  propertyId: number;
  propertyName: string;
  address: string;
  dues: number;
  lateFees: number;
  total: number;
};

type IncomeData = {
  monthly: MonthlyIncome[];
  byProperty: PropertyIncome[];
  variance: VarianceData | null;
};

type MemberHealth = {
  id: number;
  name: string;
  displayName: string;
  roomId: number | null;
  propertyId: number | null;
  propertyName: string | null;
  weeklyRate: number | null;
  totalDue: number | null;
  paymentStatus: string | null;
  lastPaymentDate: string | null;
  onTimePaymentPct: number | null;
  lateFeeEventCount: number | null;
  daysSincePayment: number | null;
  paymentOverdue: boolean;
  tier: EconTier | "unknown";
  tierLabel: string;
  trend: "improving" | "worsening" | "stable" | null;
};

type MemberHealthData = {
  members: MemberHealth[];
  tierCounts: TierCounts & { unknown: number };
  thresholds: {
    greenThresholdMin: number;
    yellowThresholdMin: number;
    orangeThresholdMin: number;
  };
};

type RoomPerf = {
  id: number;
  propertyId: number | null;
  propertyName: string;
  propertyAddress: string | null;
  name: string;
  roomType: string | null;
  sizeCategory: string | null;
  squareFootage: number | null;
  status: string | null;
  currentPrice: number;
  weeklyRate: number | null;
  memberName: string | null;
  memberId: number | null;
  revpar: number | null;
  revparHistory: { month: string; revpar: number }[];
  isUnderperforming: boolean;
};

type RoomPerfData = {
  month: string;
  rooms: RoomPerf[];
  underperformingCount: number;
};

type PropertyHealth = {
  propertyId: number;
  propertyName: string;
  propertyAddress: string | null;
  tier: EconTier;
  tierLabel: string;
  tierColor: string;
  occupancyRate: number;
  collectionRate: number | null;
  activeMembers: number;
  orangeRedMemberCount: number;
  unknownMemberCount: number;
  summary: string | null;
  notEnoughData: boolean;
};

type PropertyHealthData = {
  properties: PropertyHealth[];
};

type AiVarianceSummary = {
  month: string;
  prevMonth: string;
  variance: VarianceData | null;
  summary: string | null;
  source: "gemini" | "fallback" | "cache" | "unavailable";
  notEnoughData?: boolean;
};

type AiTrendData = {
  window: number;
  monthly: { month: string; total: number; dues: number; lateFees: number }[];
  byProperty?: { label: string; monthly: { month: string; total: number }[] }[];
  analysis: string | null;
  source: "gemini" | "fallback" | "cache" | "unavailable";
};

type AnalyticsSettings = {
  id: number;
  userId: number;
  greenThresholdMin: string;
  yellowThresholdMin: string;
  orangeThresholdMin: string;
  dynamicWeeklyRate: boolean;
  minDaysWithoutPayment: number;
  collectionRateAlertThreshold: string;
  excludeInactiveRooms: boolean;
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const TIER_COLORS: Record<EconTier, string> = {
  unknown: "#64748b",
  green: "#22c55e",
  yellow: "#eab308",
  orange: "#f97316",
  red: "#ef4444",
};

const TIER_BG: Record<EconTier, string> = {
  unknown: "bg-muted text-muted-foreground",
  green: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
  yellow: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400",
  orange: "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400",
  red: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
};

const TIER_LABELS: Record<EconTier, string> = {
  green: "Green stored balance tier",
  unknown: "Unknown balance",
  yellow: "Slightly behind",
  orange: "Moderately behind",
  red: "Seriously behind",
};

function fmt$$(n: number | null | undefined): string {
  if (n == null) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(n);
}

function fmtPct(n: number | null | undefined): string {
  if (n == null) return "—";
  return `${Math.round(n)}%`;
}

function fmtMonth(mk: string): string {
  const [y, m] = mk.split("-");
  return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString("en-US", {
    month: "short",
    year: "numeric",
  });
}

function DeltaBadge({ value, suffix = "" }: { value: number | null; suffix?: string }) {
  if (value == null) return <span className="text-muted-foreground text-sm">—</span>;
  const pos = value >= 0;
  return (
    <span
      className={`inline-flex items-center gap-0.5 text-sm font-medium ${pos ? "text-green-600" : "text-red-500"}`}
    >
      {pos ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}
      {pos ? "+" : ""}
      {value.toFixed(0)}
      {suffix}
    </span>
  );
}

function TrendIcon({ trend }: { trend: "improving" | "worsening" | "stable" | null }) {
  if (trend == null) return <span className="text-muted-foreground text-xs">Unavailable</span>;
  if (trend === "improving") return <TrendingUp className="h-4 w-4 text-green-500" />;
  if (trend === "worsening") return <TrendingDown className="h-4 w-4 text-red-500" />;
  return <Minus className="h-4 w-4 text-muted-foreground" />;
}

function KpiCard({
  title,
  value,
  delta,
  icon: Icon,
  alert,
  sub,
}: {
  title: string;
  value: string;
  delta?: number | null;
  icon: React.ElementType;
  alert?: boolean;
  sub?: string;
}) {
  return (
    <Card
      className={alert ? "border-red-400" : ""}
      data-testid={`kpi-card-${title.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <CardContent className="pt-5 pb-4">
        <div className="flex items-center justify-between mb-1">
          <p className="text-sm text-muted-foreground font-medium">{title}</p>
          <Icon className={`h-4 w-4 ${alert ? "text-red-500" : "text-muted-foreground"}`} />
        </div>
        <p className="text-2xl font-bold tracking-tight">{value}</p>
        <div className="mt-1 flex items-center gap-2 min-h-[20px]">
          {delta != null && <DeltaBadge value={delta} />}
          {sub && <span className="text-xs text-muted-foreground">{sub}</span>}
        </div>
      </CardContent>
    </Card>
  );
}

function LoadingPanel() {
  return (
    <div className="space-y-3" data-testid="loading-analytics-panel">
      <Skeleton className="h-24 w-full" />
      <Skeleton className="h-64 w-full" />
    </div>
  );
}

function EmptyPanel({ message }: { message: string }) {
  return (
    <div
      className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed py-16 text-center"
      data-testid="empty-analytics-panel"
    >
      <Info className="h-6 w-6 text-muted-foreground" />
      <p className="max-w-sm text-sm text-muted-foreground">{message}</p>
    </div>
  );
}

// ─── Variance Decomposition bar chart ────────────────────────────────────────

function VarianceDecompositionChart({ v }: { v: VarianceData }) {
  const items = [
    { label: "Calendar effect", value: v.calendarEffect, desc: "Different payment days" },
    { label: "Occupancy change", value: v.occupancyChange, desc: "Rooms occupied" },
    { label: "Pricing change", value: v.pricingChange, desc: "Avg weekly rate shift" },
    {
      label: "Collection rate",
      value: v.collectionRateChange,
      desc: v.collectionRateDrop ? "⚠ Rate dropped >5%" : "% collected vs billed",
      alert: v.collectionRateDrop,
    },
    { label: "Late fee income", value: v.lateFeeIncome, desc: "Change in late fees" },
    { label: "Other / misc", value: v.other, desc: "Unexplained remainder" },
  ];

  const max = Math.max(...items.map((i) => Math.abs(i.value)), 1);

  return (
    <div className="space-y-2.5" data-testid="variance-decomposition-chart">
      {items.map((item) => {
        const pct = (Math.abs(item.value) / max) * 100;
        const pos = item.value >= 0;
        return (
          <div key={item.label} className="flex items-center gap-3">
            <div className="w-32 text-xs text-muted-foreground shrink-0 text-right">
              {item.label}
            </div>
            <div className="flex-1 h-5 rounded overflow-hidden bg-muted/30 relative">
              {/* centre line */}
              <div className="absolute inset-y-0 left-1/2 w-px bg-muted-foreground/30" />
              {/* bar */}
              <div
                className="absolute inset-y-0 rounded"
                style={{
                  left: pos ? "50%" : `${50 - pct / 2}%`,
                  width: `${pct / 2}%`,
                  backgroundColor: pos ? "#22c55e" : "#ef4444",
                }}
              />
            </div>
            <div
              className={`w-20 text-xs font-medium text-right shrink-0 ${pos ? "text-green-600" : "text-red-500"}`}
            >
              {pos ? "+" : ""}
              {fmt$$(item.value)}
            </div>
            {item.alert && (
              <AlertTriangle className="h-3.5 w-3.5 text-orange-500 shrink-0" />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── PDF export helpers ───────────────────────────────────────────────────────

function fmtMonthShort(mk: string): string {
  const [y, m] = mk.split("-");
  return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString("en-US", {
    month: "short",
    year: "2-digit",
  });
}

/**
 * Render a 12-month RevPAR trend as a PNG data URL by drawing directly on a
 * 2D canvas. Avoids the fragility of serializing a live recharts SVG (external
 * CSS / tainted-canvas issues) and works fully offline in-browser.
 */
function buildRevparTrendImage(points: { month: string; revpar: number }[]): string | null {
  if (points.length === 0) return null;
  const W = 1000;
  const H = 360;
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;

  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, W, H);

  const padL = 78;
  const padR = 30;
  const padT = 24;
  const padB = 48;
  const plotW = W - padL - padR;
  const plotH = H - padT - padB;

  const maxV = Math.max(...points.map((p) => p.revpar), 1);
  const xFor = (i: number) =>
    padL + (points.length === 1 ? plotW / 2 : (i / (points.length - 1)) * plotW);
  const yFor = (v: number) => padT + plotH - (v / maxV) * plotH;

  // Y gridlines + labels
  ctx.font = "13px Helvetica, Arial, sans-serif";
  const ticks = 4;
  for (let t = 0; t <= ticks; t++) {
    const v = (maxV / ticks) * t;
    const y = yFor(v);
    ctx.strokeStyle = "#e5e7eb";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(padL, y);
    ctx.lineTo(W - padR, y);
    ctx.stroke();
    ctx.fillStyle = "#6b7280";
    ctx.textAlign = "right";
    ctx.textBaseline = "middle";
    ctx.fillText("$" + Math.round(v).toLocaleString(), padL - 10, y);
  }

  // Area fill
  ctx.beginPath();
  points.forEach((p, i) => {
    const x = xFor(i);
    const y = yFor(p.revpar);
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  });
  ctx.lineTo(xFor(points.length - 1), yFor(0));
  ctx.lineTo(xFor(0), yFor(0));
  ctx.closePath();
  ctx.fillStyle = "rgba(0, 82, 204, 0.14)";
  ctx.fill();

  // Trend line
  ctx.beginPath();
  points.forEach((p, i) => {
    const x = xFor(i);
    const y = yFor(p.revpar);
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  });
  ctx.strokeStyle = "rgb(0, 82, 204)";
  ctx.lineWidth = 2.5;
  ctx.stroke();

  // Data points
  ctx.fillStyle = "rgb(0, 82, 204)";
  points.forEach((p, i) => {
    ctx.beginPath();
    ctx.arc(xFor(i), yFor(p.revpar), 3.5, 0, Math.PI * 2);
    ctx.fill();
  });

  // X labels (thin out when crowded)
  ctx.fillStyle = "#6b7280";
  ctx.textAlign = "center";
  ctx.textBaseline = "top";
  ctx.font = "12px Helvetica, Arial, sans-serif";
  const everyOther = points.length > 8;
  points.forEach((p, i) => {
    if (everyOther && i % 2 !== 0 && i !== points.length - 1) return;
    ctx.fillText(fmtMonthShort(p.month), xFor(i), H - padB + 10);
  });

  return canvas.toDataURL("image/png");
}

// ─── AI plain-English summary card ────────────────────────────────────────────

export function AiSummaryCard() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const { data, isLoading, error } = useQuery<AiSummaryData>({
    queryKey: ["/api/app/analytics/summary"],
    staleTime: 5 * 60_000,
  });

  const refresh = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("GET", "/api/app/analytics/summary?refresh=1");
      return (await res.json()) as AiSummaryData;
    },
    onSuccess: (fresh) => {
      qc.setQueryData(["/api/app/analytics/summary"], fresh);
    },
    onError: (err) => {
      toast({
        title: "Couldn't refresh summary",
        description: parseErrorMessage(err, "Please try again in a moment."),
        variant: "destructive",
      });
    },
  });

  if (isLoading) {
    return <Skeleton className="h-28" data-testid="ai-summary-loading" />;
  }

  // Graceful fallback: AI unavailable (no key, empty result, or fetch error).
  // The rule-based cards below still render, so we keep this quiet and subtle.
  if (error || !data || !data.available || !data.summary) {
    return (
      <Card className="border-dashed" data-testid="ai-summary-unavailable">
        <CardContent className="flex items-center gap-3 py-4 text-sm text-muted-foreground">
          <Sparkles className="h-4 w-4 shrink-0" />
          <span>
            AI summary is unavailable right now — the metrics and explanation cards below are
            always up to date.
          </span>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      className="border-brand/30 bg-brand-muted/40"
      data-testid="ai-summary-card"
    >
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-brand" />
            AI Summary
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 gap-1.5 text-xs text-muted-foreground"
            onClick={() => refresh.mutate()}
            disabled={refresh.isPending}
            data-testid="button-refresh-ai-summary"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${refresh.isPending ? "animate-spin" : ""}`} />
            {refresh.isPending ? "Regenerating…" : "Regenerate"}
          </Button>
        </div>
        <CardDescription data-testid="text-ai-summary-month">
          Plain-English read on {fmtMonth(data.month)}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <p
          className="text-sm leading-relaxed text-foreground/90 whitespace-pre-line"
          data-testid="text-ai-summary"
        >
          {data.summary}
        </p>
      </CardContent>
    </Card>
  );
}

// ─── Tab: Overview ────────────────────────────────────────────────────────────

const HEALTH_TIER_BG: Record<EconTier, string> = {
  unknown: "bg-muted border-border text-muted-foreground",
  green: "bg-green-50 border-green-200 text-green-700 dark:bg-green-900/20 dark:border-green-800 dark:text-green-400",
  yellow: "bg-yellow-50 border-yellow-200 text-yellow-700 dark:bg-yellow-900/20 dark:border-yellow-800 dark:text-yellow-400",
  orange: "bg-orange-50 border-orange-200 text-orange-700 dark:bg-orange-900/20 dark:border-orange-800 dark:text-orange-400",
  red: "bg-red-50 border-red-200 text-red-700 dark:bg-red-900/20 dark:border-red-800 dark:text-red-400",
};

const HEALTH_DOT: Record<EconTier, string> = {
  unknown: "bg-slate-500",
  green: "bg-green-500",
  yellow: "bg-yellow-400",
  orange: "bg-orange-500",
  red: "bg-red-500",
};

const HEALTH_TIER_ORDER: Record<PropertyHealth["tier"], number> = {
  unknown: 4,
  red: 0,
  orange: 1,
  yellow: 2,
  green: 3,
};

function comparePropertyHealth(a: PropertyHealth, b: PropertyHealth): number {
  const tierDifference = HEALTH_TIER_ORDER[a.tier] - HEALTH_TIER_ORDER[b.tier];
  if (tierDifference !== 0) return tierDifference;

  const aName = String(a.propertyName ?? "").toLowerCase();
  const bName = String(b.propertyName ?? "").toLowerCase();
  if (aName < bName) return -1;
  if (aName > bName) return 1;
  return a.propertyId - b.propertyId;
}

export function OverviewTab() {
  const { toast } = useToast();
  const [isExporting, setIsExporting] = useState(false);
  const { data, isLoading, error } = useQuery<OverviewData>({
    queryKey: ["/api/app/analytics/overview"],
  });
  const {
    data: healthData,
    isLoading: healthLoading,
    isError: healthError,
  } = useQuery<PropertyHealthData>({
    queryKey: ["/api/app/analytics/property-health"],
    retry: false,
  });

  const handleExportPDF = async () => {
    if (!data) return;
    setIsExporting(true);
    try {
      const [incomeRes, roomsRes] = await Promise.all([
        fetch("/api/app/analytics/income?months=12", { credentials: "include" }),
        fetch("/api/app/analytics/room-performance?months=6", { credentials: "include" }),
      ]);
      if (!incomeRes.ok || !roomsRes.ok) {
        throw new Error("Failed to load report data");
      }
      const income: IncomeData = await incomeRes.json();
      const roomData: RoomPerfData = await roomsRes.json();

      const { default: jsPDF } = await import("jspdf");
      const { default: autoTable } = await import("jspdf-autotable");

      const doc = new jsPDF();
      const primaryBlue: [number, number, number] = [0, 82, 204];
      const accentOrange: [number, number, number] = [255, 165, 0];
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const exportDate = new Date().toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
      const lastAutoY = () =>
        (doc as typeof doc & { lastAutoTable: { finalY: number } }).lastAutoTable.finalY;

      // Header band
      doc.setFillColor(...primaryBlue);
      doc.rect(0, 0, pageWidth, 35, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(22);
      doc.setFont("helvetica", "bold");
      doc.text("PadPulse", 14, 18);
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.text("Portfolio Analytics Report", 14, 26);
      doc.text(exportDate, pageWidth - 14, 18, { align: "right" });
      doc.text(fmtMonth(data.month), pageWidth - 14, 26, { align: "right" });

      let y = 45;

      const sectionHeader = (title: string, color: [number, number, number] = accentOrange) => {
        if (y > pageHeight - 30) {
          doc.addPage();
          y = 20;
        }
        doc.setFillColor(...color);
        doc.rect(14, y, pageWidth - 28, 8, "F");
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(11);
        doc.setFont("helvetica", "bold");
        doc.text(title, 17, y + 5.5);
        doc.setTextColor(0, 0, 0);
        doc.setFont("helvetica", "normal");
        y += 12;
      };

      // ── Portfolio summary ──
      sectionHeader("Portfolio Summary");
      autoTable(doc, {
        startY: y,
        head: [],
        body: [
          ["Reporting Month", fmtMonth(data.month)],
          ["RevPAR (per room / mo)", fmt$$(data.revpar)],
          ["Collection Rate", fmtPct(data.collectionRate)],
          [
            "Occupancy",
            `${fmtPct(data.occupancyRate)} (${data.occupiedRooms}/${data.totalRooms} rooms)`,
          ],
          ["Active Members", String(data.activeMembers)],
          ["Vacant Rooms", String(data.vacantRooms)],
          ["Revenue Collected", fmt$$(data.totalCollected)],
          ["Dues Collected", fmt$$(data.dues)],
          ["Late Fees", fmt$$(data.lateFees)],
          ["Booking Fees", fmt$$(data.bookingFees)],
        ],
        theme: "striped",
        styles: { fontSize: 9, cellPadding: 2 },
        columnStyles: { 0: { fontStyle: "bold", cellWidth: 70 }, 1: { halign: "right" } },
        margin: { left: 14, right: 14 },
      });
      y = lastAutoY() + 8;

      // ── 12-month RevPAR trend chart ──
      const trendPoints = income.monthly.map((m) => ({
        month: m.month,
        revpar: data.totalRooms > 0 ? Math.round((m.total / data.totalRooms) * 100) / 100 : 0,
      }));
      const trendImg = buildRevparTrendImage(trendPoints);
      if (trendImg) {
        const imgW = pageWidth - 28;
        const imgH = imgW * (360 / 1000);
        if (y + 12 + imgH > pageHeight - 16) {
          doc.addPage();
          y = 20;
        }
        sectionHeader("12-Month RevPAR Trend");
        doc.addImage(trendImg, "PNG", 14, y, imgW, imgH);
        y += imgH + 8;
      }

      // ── By property ──
      sectionHeader("Income by Property — Current Month");
      if (income.byProperty.length === 0) {
        doc.setFontSize(9);
        doc.setTextColor(100, 100, 100);
        doc.text("No income records for this month.", 17, y);
        doc.setTextColor(0, 0, 0);
        y += 8;
      } else {
        autoTable(doc, {
          startY: y,
          head: [["Property", "Dues", "Late Fees", "Total"]],
          body: income.byProperty.map((p) => [
            p.address ? `${p.propertyName}\n${p.address}` : p.propertyName,
            fmt$$(p.dues),
            fmt$$(p.lateFees),
            fmt$$(p.total),
          ]),
          foot: [
            [
              "Portfolio Total",
              fmt$$(income.byProperty.reduce((s, p) => s + p.dues, 0)),
              fmt$$(income.byProperty.reduce((s, p) => s + p.lateFees, 0)),
              fmt$$(income.byProperty.reduce((s, p) => s + p.total, 0)),
            ],
          ],
          theme: "striped",
          styles: { fontSize: 8, cellPadding: 2 },
          headStyles: { fillColor: primaryBlue },
          footStyles: { fillColor: [230, 240, 255], textColor: 0, fontStyle: "bold" },
          columnStyles: {
            1: { halign: "right" },
            2: { halign: "right" },
            3: { halign: "right" },
          },
          margin: { left: 14, right: 14 },
        });
        y = lastAutoY() + 8;
      }

      // ── Underperforming rooms ──
      const underperforming = roomData.rooms.filter((r) => r.isUnderperforming);
      sectionHeader(
        `Underperforming Rooms (${underperforming.length})`,
        [220, 38, 38],
      );
      if (underperforming.length === 0) {
        doc.setFontSize(9);
        doc.setTextColor(100, 100, 100);
        doc.text("No chronically underperforming rooms.", 17, y);
        doc.setTextColor(0, 0, 0);
      } else {
        autoTable(doc, {
          startY: y,
          head: [["Room", "Property", "Current Price", "RevPAR / day", "Status"]],
          body: underperforming.map((r) => [
            r.name,
            r.propertyName,
            fmt$$(r.currentPrice),
            fmt$$(r.revpar),
            r.status ?? "—",
          ]),
          theme: "striped",
          styles: { fontSize: 8, cellPadding: 2 },
          headStyles: { fillColor: [185, 28, 28] as [number, number, number] },
          columnStyles: { 2: { halign: "right" }, 3: { halign: "right" } },
          margin: { left: 14, right: 14 },
        });
      }

      // Footer on every page
      const pageCount = doc.internal.pages.length - 1;
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(128, 128, 128);
        doc.text(
          `Generated by PadPulse | ${exportDate}`,
          pageWidth / 2,
          pageHeight - 8,
          { align: "center" },
        );
      }

      doc.save(`portfolio-analytics-${data.month}.pdf`);
    } catch (err) {
      toast({
        title: "Export failed",
        description: parseErrorMessage(err, "Could not generate the PDF report."),
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {Array.from({ length: 8 }).map((_, i) => (
          <Skeleton key={i} className="h-28" />
        ))}
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="text-center py-16 text-muted-foreground">
        Failed to load overview data.
      </div>
    );
  }

  const totalMembers = Object.values(data.tierCounts).reduce((s, n) => s + n, 0);
  const tierPieData = (["green", "yellow", "orange", "red", "unknown"] as EconTier[])
    .map((t) => ({ name: TIER_LABELS[t], value: data.tierCounts[t], fill: TIER_COLORS[t] }))
    .filter((d) => d.value > 0);

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground" data-testid="overview-payment-evidence">
        Payment tiers use stored member balances, not live PadSplit verification. Payment-field freshness is unverified.
        Zero or credit balances mean no stored balance due, not verified current payment. Unknown balances are not classified as healthy or delinquent.
      </p>
      {/* Export action */}
      <div className="flex items-center justify-end">
        <Button
          size="sm"
          variant="outline"
          onClick={handleExportPDF}
          disabled={isExporting}
          data-testid="button-export-pdf"
        >
          <Download className="h-4 w-4 mr-2" />
          {isExporting ? "Generating…" : "Export PDF"}
        </Button>
      </div>

      {/* AI plain-English summary (Phase 3) */}
      <AiSummaryCard />

      {/* AI Insights */}
      <AiCoachingInsightsPanel />

      {/* Not-enough-data notice: fewer than two consecutive imported months */}
      {data.notEnoughData && (
        <div
          className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 dark:border-amber-900/50 dark:bg-amber-950/30 px-4 py-2.5 text-sm text-amber-800 dark:text-amber-300"
          data-testid="not-enough-data-notice"
        >
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
          <span>
            Showing <strong>{fmtMonth(data.month)}</strong>, your most recent fully-imported month.
            Month-over-month comparisons need two consecutive imported months — import another month
            to unlock them.
          </span>
        </div>
      )}

      {/* Collection rate alert */}
      {data.collectionRateAlert && (
        <div
          className="flex items-center gap-2 rounded-lg border border-red-300 bg-red-50 dark:bg-red-900/20 px-4 py-2.5 text-sm text-red-700 dark:text-red-400"
          data-testid="collection-rate-alert"
        >
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span>
            Collection rate is <strong>{fmtPct(data.collectionRate)}</strong> — below your
            alert threshold of {fmtPct(data.alertThreshold)}
          </span>
        </div>
      )}

      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard
          title="RevPAR"
          value={fmt$$(data.revpar)}
          delta={data.revparChange}
          icon={DollarSign}
          sub="per room / mo"
        />
        <KpiCard
          title="Collection Rate"
          value={fmtPct(data.collectionRate)}
          icon={Activity}
          alert={data.collectionRateAlert}
          sub={`${fmtPct(data.alertThreshold)} threshold`}
        />
        <KpiCard
          title="Occupancy"
          value={fmtPct(data.occupancyRate)}
          icon={Home}
          sub={`${data.occupiedRooms}/${data.totalRooms} rooms`}
        />
        <KpiCard
          title="Active Members"
          value={String(data.activeMembers)}
          icon={Users}
          sub={`${data.vacantRooms} vacant room${data.vacantRooms !== 1 ? "s" : ""}`}
        />
        <KpiCard
          title="Revenue Collected"
          value={fmt$$(data.totalCollected)}
          delta={data.totalCollected - data.prevTotalCollected}
          icon={DollarSign}
          sub={`vs ${fmt$$(data.prevTotalCollected)} last mo`}
        />
        <KpiCard title="Dues Collected" value={fmt$$(data.dues)} icon={DollarSign} />
        <KpiCard title="Late Fees" value={fmt$$(data.lateFees)} icon={AlertTriangle} />
        <KpiCard title="Booking Fees" value={fmt$$(data.bookingFees)} icon={DollarSign} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Member tier donut */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Member Payment Health</CardTitle>
            <CardDescription>
              {totalMembers} active members — {fmtMonth(data.month)}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {totalMembers === 0 ? (
              <p className="text-center py-8 text-muted-foreground text-sm">No active members</p>
            ) : (
              <div className="flex items-center gap-6">
                <div className="shrink-0">
                  <PieChart width={130} height={130}>
                    <Pie
                      data={tierPieData}
                      cx={60}
                      cy={60}
                      innerRadius={35}
                      outerRadius={60}
                      dataKey="value"
                      paddingAngle={2}
                    >
                      {tierPieData.map((entry) => (
                        <Cell key={entry.name} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v) => [`${v} members`]} />
                  </PieChart>
                </div>
                <div className="space-y-2 flex-1">
                  {(["green", "yellow", "orange", "red", "unknown"] as EconTier[]).map((tier) => {
                    const count = data.tierCounts[tier] ?? 0;
                    const pct = totalMembers > 0 ? (count / totalMembers) * 100 : 0;
                    return (
                      <div key={tier} className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full shrink-0"
                          style={{ background: TIER_COLORS[tier] }}
                        />
                        <span className="text-xs text-muted-foreground flex-1">
                          {TIER_LABELS[tier]}
                        </span>
                        <span className="text-xs font-medium">{count}</span>
                        <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full"
                            style={{ width: `${pct}%`, background: TIER_COLORS[tier] }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Variance decomposition */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Revenue Change vs Last Month</CardTitle>
            <CardDescription>
              {data.hasComparison && data.variance ? (
                <>
                  <span
                    className={
                      data.variance.totalChange >= 0 ? "text-green-600" : "text-red-500"
                    }
                  >
                    {data.variance.totalChange >= 0 ? "+" : ""}
                    {fmt$$(data.variance.totalChange)}
                  </span>{" "}
                  total — decomposed into drivers
                </>
              ) : (
                <>{fmtMonth(data.prevMonth)} not yet imported</>
              )}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {data.hasComparison && data.variance ? (
              <VarianceDecompositionChart v={data.variance} />
            ) : (
              <p
                className="py-8 text-center text-sm text-muted-foreground"
                data-testid="variance-not-enough-data"
              >
                Not enough data — import {fmtMonth(data.prevMonth)} to compare it
                with {fmtMonth(data.month)}.
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Property Health Grid */}
      <Card data-testid="property-health-grid">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Property Health Scores</CardTitle>
            <CardDescription>
              Imported-month health tier per property — based on occupancy, collection rate, and stored member balances.
              Incomplete payment evidence cannot establish Healthy; known risk flags still apply.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {healthLoading ? (
              <p
                className="py-6 text-center text-sm text-muted-foreground"
                data-testid="property-health-loading"
                role="status"
              >
                Loading property health scores…
              </p>
            ) : healthError ? (
              <p
                className="py-6 text-center text-sm text-muted-foreground"
                data-testid="property-health-error"
                role="alert"
              >
                Property health scores are unavailable right now. Refresh the page to try again.
              </p>
            ) : !healthData?.properties.length ? (
              <p
                className="py-6 text-center text-sm text-muted-foreground"
                data-testid="property-health-empty"
              >
                No eligible properties are available to score yet. Scores will appear when properties have health data.
              </p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {[...healthData.properties].sort(comparePropertyHealth).map((p) => (
                  <Link key={p.propertyId} href={`/app/properties/${p.propertyId}`}>
                    <div
                      className={`rounded-lg border p-3 transition-all hover:opacity-80 cursor-pointer ${HEALTH_TIER_BG[p.tier]}`}
                      data-testid={`property-health-card-${p.propertyId}`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${HEALTH_DOT[p.tier]}`} />
                        <span className="text-xs font-semibold">{p.tierLabel}</span>
                      </div>
                      <div className="font-medium text-sm truncate">{p.propertyName}</div>
                      <div className="mt-1.5 flex items-center gap-3 text-xs opacity-80">
                        <span>Occ {Math.round(p.occupancyRate)}%</span>
                        {p.collectionRate != null && (
                          <span>CR {Math.round(p.collectionRate)}%</span>
                        )}
                        {p.activeMembers > 0 && (
                          <span>{p.activeMembers} mbr{p.activeMembers !== 1 ? "s" : ""}</span>
                        )}
                      </div>
                      <p className="mt-1 text-xs" data-testid={`property-health-unknown-${p.propertyId}`}>
                        Unknown stored balances: {p.unknownMemberCount ?? 0} of {p.activeMembers}
                      </p>
                      {p.summary && (
                        <p className="mt-2 text-xs opacity-75 line-clamp-2">{p.summary}</p>
                      )}
                      {!p.summary && p.notEnoughData && (
                        <p
                          className="mt-2 text-xs opacity-75"
                          data-testid={`property-health-summary-data-${p.propertyId}`}
                        >
                          Health score is available. The summary needs income imported for two
                          consecutive months to compare month over month. Import another month to
                          unlock it.
                        </p>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
      </Card>

      {/* Booking-request outcomes — hidden entirely for accounts without the
          booking_requests entitlement (the card's fetch 403s and it renders
          null). Note this tab is beta-gated while the same numbers are
          visible ungated on the booking page's Stats tab — intentional. */}
      <BookingRequestsOverviewCard />
    </div>
  );
}

// ─── Tab: Income Analysis ─────────────────────────────────────────────────────

function IncomeAnalysisTab() {
  const [months, setMonths] = useState(6);
  const [trendWindow, setTrendWindow] = useState(3);
  const { data, isLoading, error } = useQuery<IncomeData>({
    queryKey: ["/api/app/analytics/income", months],
    queryFn: () =>
      fetch(`/api/app/analytics/income?months=${months}`, { credentials: "include" }).then(
        (r) => r.json(),
      ),
  });
  const { data: aiVariance, isLoading: aiVarianceLoading } = useQuery<AiVarianceSummary>({
    queryKey: ["/api/app/analytics/ai-variance"],
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
  const { data: trendData, isLoading: trendLoading } = useQuery<AiTrendData>({
    queryKey: ["/api/app/analytics/ai-trend", trendWindow],
    queryFn: () =>
      fetch(`/api/app/analytics/ai-trend?window=${trendWindow}`, {
        credentials: "include",
      }).then((r) => r.json()),
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-64" />
        <Skeleton className="h-48" />
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="text-center py-16 text-muted-foreground">
        Failed to load income data.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Lookback selector */}
      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground">Lookback:</span>
        {[3, 6, 12].map((m) => (
          <Button
            key={m}
            variant={months === m ? "default" : "outline"}
            size="sm"
            onClick={() => setMonths(m)}
            data-testid={`income-months-${m}`}
          >
            {m}mo
          </Button>
        ))}
      </div>

      {/* Stacked area chart */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Monthly Income Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart
              data={data.monthly}
              margin={{ top: 4, right: 4, left: 0, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis
                dataKey="month"
                tickFormatter={(v) => fmtMonth(v)}
                tick={{ fontSize: 11 }}
              />
              <YAxis
                tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
                tick={{ fontSize: 11 }}
              />
              <Tooltip
                formatter={(v: number, name: string) => [fmt$$(v), name]}
                labelFormatter={(l) => fmtMonth(l as string)}
              />
              <Legend />
              <Area
                type="monotone"
                dataKey="dues"
                name="Dues"
                stackId="1"
                stroke="#3b82f6"
                fill="#3b82f6"
                fillOpacity={0.6}
              />
              <Area
                type="monotone"
                dataKey="lateFees"
                name="Late Fees"
                stackId="1"
                stroke="#f97316"
                fill="#f97316"
                fillOpacity={0.6}
              />
              <Area
                type="monotone"
                dataKey="bookingFees"
                name="Booking Fees"
                stackId="1"
                stroke="#8b5cf6"
                fill="#8b5cf6"
                fillOpacity={0.6}
              />
              <Area
                type="monotone"
                dataKey="otherIncome"
                name="Other"
                stackId="1"
                stroke="#6b7280"
                fill="#6b7280"
                fillOpacity={0.4}
              />
            </AreaChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* MoM variance */}
      {data.variance && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Month-over-Month Revenue Drivers</CardTitle>
            <CardDescription>
              <span
                className={
                  data.variance.totalChange >= 0 ? "text-green-600" : "text-red-500"
                }
              >
                {data.variance.totalChange >= 0 ? "+" : ""}
                {fmt$$(data.variance.totalChange)}
              </span>{" "}
              vs prior month
            </CardDescription>
          </CardHeader>
          <CardContent>
            <VarianceDecompositionChart v={data.variance} />
          </CardContent>
        </Card>
      )}

      {/* AI Variance Summary */}
      {(aiVarianceLoading || aiVariance) && (
        <Card data-testid="ai-variance-summary-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Activity className="h-4 w-4 text-brand" />
              AI Revenue Variance Summary
            </CardTitle>
            {aiVariance && (
              <CardDescription>
                {aiVariance.notEnoughData
                  ? "Not enough completed earnings data for a comparison"
                  : `${fmtMonth(aiVariance.month)} vs ${fmtMonth(aiVariance.prevMonth)}`}
                {aiVariance.source === "fallback" && (
                  <span className="ml-2 text-xs text-muted-foreground">(deterministic summary)</span>
                )}
              </CardDescription>
            )}
          </CardHeader>
          <CardContent>
            {aiVarianceLoading ? (
              <Skeleton className="h-16 w-full" />
            ) : aiVariance?.summary ? (
              <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap">{aiVariance.summary}</p>
            ) : null}
          </CardContent>
        </Card>
      )}

      {/* AI Trend Analysis */}
      <Card data-testid="ai-trend-analysis-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-brand" />
            AI Trend Analysis
          </CardTitle>
          <CardDescription>
            Multi-window revenue trend with per-property insights
            {trendData?.source === "fallback" && (
              <span className="ml-2 text-xs text-muted-foreground" data-testid="trend-source-fallback">
                (deterministic summary)
              </span>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* Window selector */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Window:</span>
            {([1, 3, 6] as const).map((w) => (
              <Button
                key={w}
                variant={trendWindow === w ? "default" : "outline"}
                size="sm"
                onClick={() => setTrendWindow(w)}
                data-testid={`trend-window-${w}`}
              >
                {w}mo
              </Button>
            ))}
          </div>

          {trendLoading ? (
            <Skeleton className="h-20 w-full" />
          ) : trendData?.analysis ? (
            <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap">
              {trendData.analysis}
            </p>
          ) : trendData && !trendData.analysis ? (
            <p className="text-sm text-muted-foreground italic">
              Trend analysis unavailable — configure a Gemini API key to enable AI summaries.
            </p>
          ) : null}

          {/* Sparkline totals for selected window */}
          {trendData && trendData.monthly.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-1 pr-3 font-medium text-muted-foreground">Month</th>
                    <th className="text-right py-1 pr-3 font-medium text-muted-foreground">Total</th>
                    <th className="text-right py-1 font-medium text-muted-foreground">Late Fees</th>
                  </tr>
                </thead>
                <tbody>
                  {trendData.monthly.map((m) => (
                    <tr key={m.month} className="border-b last:border-0">
                      <td className="py-1 pr-3 text-muted-foreground">{fmtMonth(m.month)}</td>
                      <td className="text-right py-1 pr-3 font-medium">{fmt$$(m.total)}</td>
                      <td className="text-right py-1 text-orange-600">{m.lateFees > 0 ? fmt$$(m.lateFees) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {trendData?.source === "unavailable" && (
            <p className="text-xs text-muted-foreground italic">
              AI analysis requires a Gemini API key.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Per-property table */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Current Month — By Property</CardTitle>
        </CardHeader>
        <CardContent>
          {data.byProperty.length === 0 ? (
            <p className="text-center py-8 text-sm text-muted-foreground">
              No income records for this month.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 pr-4 font-medium text-muted-foreground">
                      Property
                    </th>
                    <th className="text-right py-2 pr-4 font-medium text-muted-foreground">
                      Dues
                    </th>
                    <th className="text-right py-2 pr-4 font-medium text-muted-foreground">
                      Late Fees
                    </th>
                    <th className="text-right py-2 font-medium text-muted-foreground">
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.byProperty.map((p) => (
                    <tr
                      key={p.propertyId}
                      className="border-b last:border-0"
                      data-testid={`income-property-${p.propertyId}`}
                    >
                      <td className="py-2 pr-4">
                        <div className="font-medium">{p.propertyName}</div>
                        {p.address && (
                          <div className="text-xs text-muted-foreground">{p.address}</div>
                        )}
                      </td>
                      <td className="text-right py-2 pr-4">{fmt$$(p.dues)}</td>
                      <td className="text-right py-2 pr-4">{fmt$$(p.lateFees)}</td>
                      <td className="text-right py-2 font-semibold">{fmt$$(p.total)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t">
                    <td className="py-2 pr-4 font-semibold">Portfolio Total</td>
                    <td className="text-right py-2 pr-4 font-semibold">
                      {fmt$$(data.byProperty.reduce((s, p) => s + p.dues, 0))}
                    </td>
                    <td className="text-right py-2 pr-4 font-semibold">
                      {fmt$$(data.byProperty.reduce((s, p) => s + p.lateFees, 0))}
                    </td>
                    <td className="text-right py-2 font-bold">
                      {fmt$$(data.byProperty.reduce((s, p) => s + p.total, 0))}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Tab: Member Health ───────────────────────────────────────────────────────

export function MemberHealthTab() {
  const [tierFilter, setTierFilter] = useState<EconTier | "unknown" | "all">("all");
  const [search, setSearch] = useState("");
  const sort = useAnalyticsSort<"displayName" | "propertyName" | "tier" | "weeklyRate" | "totalDue" | "daysSincePayment" | "trend">(null);

  const { data, isLoading, error } = useQuery<MemberHealthData>({
    queryKey: ["/api/app/analytics/member-health"],
  });

  const sorted = useMemo(() => {
    if (!data) return [];
    let list = data.members;
    if (tierFilter !== "all") list = list.filter((m) => m.tier === tierFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (m) =>
          String(m.displayName ?? "").toLowerCase().includes(q) ||
          String(m.propertyName ?? "").toLowerCase().includes(q),
      );
    }
    const field = sort.field;
    if (!field) return list;
    return sortAnalyticsRows(list, (m) => field === "tier" ? m.tier === "unknown" ? null : memberTierRank[m.tier]
      : field === "trend" ? m.trend == null ? null : memberTrendRank[m.trend] : m[field], sort.direction);
  }, [data, tierFilter, search, sort.field, sort.direction]);

  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-16" />
        ))}
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="text-center py-16 text-muted-foreground">
        Failed to load member health data.
      </div>
    );
  }

  const totalMembers = Object.values(data.tierCounts).reduce((s, n) => s + n, 0);

  return (
    <div className="space-y-5">
      <p className="text-sm text-muted-foreground">
        Stored payment information — not a live PadSplit verification. Payment-field freshness is unverified.
        Zero or credit balances mean no balance due in the stored data, not a verified current payment status.
        Payment dates reflect stored records only; balance trends and on-time payment metrics are unavailable without supported history.
      </p>
      <p className="text-xs text-muted-foreground">Tier sorts from least to most balance risk; trend sorts worsening, stable, improving. Unknown values stay last.</p>
      {/* Tier summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {(["green", "yellow", "orange", "red"] as EconTier[]).map((tier) => (
          <button
            key={tier}
            onClick={() => setTierFilter(tierFilter === tier ? "all" : tier)}
            className={`rounded-lg border p-3 text-left transition-all hover:bg-muted/40 ${tierFilter === tier ? "ring-2 ring-offset-1" : ""}`}
            style={tierFilter === tier ? { borderColor: TIER_COLORS[tier], outlineColor: TIER_COLORS[tier] } : {}}
            data-testid={`tier-filter-${tier}`}
          >
            <div className="flex items-center gap-2 mb-1">
              <div
                className="w-2.5 h-2.5 rounded-full"
                style={{ background: TIER_COLORS[tier] }}
              />
              <span className="text-xs font-medium text-muted-foreground">
                {TIER_LABELS[tier]}
              </span>
            </div>
            <div className="text-2xl font-bold" style={{ color: TIER_COLORS[tier] }}>
              {data.tierCounts[tier]}
            </div>
          </button>
        ))}
      </div>
      <button type="button" data-testid="tier-filter-unknown"
        onClick={() => setTierFilter(tierFilter === "unknown" ? "all" : "unknown")}
        className={`rounded-lg border p-3 text-left hover:bg-muted/40 ${tierFilter === "unknown" ? "ring-2" : ""}`}>
        Unknown balance: {data.tierCounts.unknown ?? 0}
      </button>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <Input
          placeholder="Search by name or property…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-56"
          data-testid="member-health-search"
        />
        {tierFilter !== "all" && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setTierFilter("all")}
            data-testid="member-health-clear-filter"
          >
            Clear filter
          </Button>
        )}
        <span className="ml-auto text-xs text-muted-foreground">
          {sorted.length} of {totalMembers} members
        </span>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full text-sm">
          <thead className="bg-muted/40">
            <tr>
              <AnalyticsSortHeader label="Member" column="displayName" sort={sort} />
              <AnalyticsSortHeader label="Property" column="propertyName" sort={sort} />
              <AnalyticsSortHeader label="Tier" column="tier" sort={sort} />
              <AnalyticsSortHeader label="Rate/wk" column="weeklyRate" sort={sort} />
              <AnalyticsSortHeader label="Stored Balance Due" column="totalDue" sort={sort} />
              <AnalyticsSortHeader label="Days Since Stored Payment" column="daysSincePayment" sort={sort} />
              <AnalyticsSortHeader label="Trend" column="trend" sort={sort} />
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 ? (
              <tr>
                <td colSpan={7} className="text-center py-10 text-muted-foreground">
                  No members match your filter.
                </td>
              </tr>
            ) : (
              sorted.map((m) => (
                <tr
                  key={m.id}
                  className="border-t hover:bg-muted/20 transition-colors"
                  data-testid={`member-row-${m.id}`}
                >
                  <td className="py-3 px-4">
                    <Link href={`/app/members/${m.id}`} className="font-medium text-brand hover:underline focus-visible:outline">{m.displayName}</Link>
                  </td>
                  <td className="py-3 px-4 text-muted-foreground text-xs">
                    {m.propertyName ?? "—"}
                  </td>
                  <td className="py-3 px-4 text-center">
                    <Badge className={m.tier === "unknown" ? "bg-muted text-muted-foreground" : TIER_BG[m.tier]}>{m.tierLabel}</Badge>
                  </td>
                  <td className="py-3 px-4 text-right">{fmt$$(m.weeklyRate)}</td>
                  <td
                    className={`py-3 px-4 text-right font-medium ${m.totalDue != null && m.totalDue > 0 ? "text-red-600" : "text-muted-foreground"}`}
                  >
                    {m.totalDue == null ? "Unknown" : m.totalDue > 0 ? fmt$$(m.totalDue) : `${fmt$$(m.totalDue)} — No stored balance due`}
                  </td>
                  <td
                    className={`py-3 px-4 text-right text-xs ${m.paymentOverdue ? "text-red-600 font-semibold" : "text-muted-foreground"}`}
                  >
                    {m.daysSincePayment != null ? `${m.daysSincePayment}d` : "—"}
                    {m.paymentOverdue && " ⚠"}
                  </td>
                  <td className="py-3 px-4 text-center">
                    <TrendIcon trend={m.trend} />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Tab: Room Performance ────────────────────────────────────────────────────

export function RoomPerformanceTab() {
  const [months, setMonths] = useState(3);
  const sort = useAnalyticsSort<"name" | "propertyName" | "memberName" | "currentPrice" | "revpar" | "trend">(null);
  const { data, isLoading, error } = useQuery<RoomPerfData>({
    queryKey: ["/api/app/analytics/room-performance", months],
    queryFn: () =>
      fetch(`/api/app/analytics/room-performance?months=${months}`, {
        credentials: "include",
      }).then((r) => r.json()),
  });

  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-14" />
        ))}
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="text-center py-16 text-muted-foreground">
        Failed to load room performance data.
      </div>
    );
  }

  const maxRevPAR = Math.max(...data.rooms.map((r) => r.revpar ?? 0), 1);
  const field = sort.field;
  const sorted = field ? sortAnalyticsRows(data.rooms, (r) => field === "trend" ? roomTrendDelta(r.revparHistory) : r[field], sort.direction) : data.rooms;

  return (
    <div className="space-y-5">
      <p className="text-xs text-muted-foreground">Trend sorts by oldest-to-latest available RevPAR change. Insufficient history and unknown values stay last.</p>
      {/* Header */}
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <p className="text-sm text-muted-foreground">
            {data.rooms.length} rooms tracked — {fmtMonth(data.month)}
          </p>
          {data.underperformingCount > 0 && (
            <p className="text-xs text-orange-600 font-medium mt-0.5">
              <AlertTriangle className="h-3.5 w-3.5 inline mr-1" />
              {data.underperformingCount} room
              {data.underperformingCount !== 1 ? "s" : ""} chronically underperforming
            </p>
          )}
        </div>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-sm text-muted-foreground">History:</span>
          {[2, 3, 6].map((m) => (
            <Button
              key={m}
              variant={months === m ? "default" : "outline"}
              size="sm"
              onClick={() => setMonths(m)}
              data-testid={`room-perf-months-${m}`}
            >
              {m}mo
            </Button>
          ))}
        </div>
      </div>

      {data.rooms.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          No billable rooms found.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead className="bg-muted/40">
              <tr>
                <AnalyticsSortHeader label="Room" column="name" sort={sort} />
                <AnalyticsSortHeader label="Property" column="propertyName" sort={sort} />
                <AnalyticsSortHeader label="Member" column="memberName" sort={sort} />
                <AnalyticsSortHeader label="List Price" column="currentPrice" sort={sort} />
                <AnalyticsSortHeader label="RevPAR/day" column="revpar" sort={sort} />
                <AnalyticsSortHeader label="Trend" column="trend" sort={sort} />
              </tr>
            </thead>
            <tbody>
              {sorted.map((r) => (
                <tr
                  key={r.id}
                  className={`border-t transition-colors ${r.isUnderperforming ? "bg-orange-50/50 dark:bg-orange-900/10 hover:bg-orange-50 dark:hover:bg-orange-900/20" : "hover:bg-muted/20"}`}
                  data-testid={`room-row-${r.id}`}
                >
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-1.5">
                      {r.isUnderperforming && (
                        <AlertTriangle className="h-3.5 w-3.5 text-orange-500 shrink-0" />
                      )}
                      <div>
                        <div className="font-medium">{r.name}</div>
                        {r.sizeCategory && (
                          <div className="text-xs text-muted-foreground capitalize">
                            {r.sizeCategory}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-muted-foreground text-xs">
                    {r.propertyName}
                  </td>
                  <td className="py-3 px-4 text-xs">
                    {r.memberName ? (
                      <span>{r.memberName}</span>
                    ) : (
                      <span className="text-muted-foreground italic">Vacant</span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-right">{fmt$$(r.currentPrice)}/wk</td>
                  <td className="py-3 px-4 text-right">
                    {r.revpar != null ? (
                      <div>
                        <div className="font-semibold">{fmt$$(r.revpar)}</div>
                        <div className="h-1.5 rounded-full bg-muted mt-1 overflow-hidden">
                          <div
                            className="h-full rounded-full bg-brand"
                            style={{
                              width: `${Math.min(100, (r.revpar / maxRevPAR) * 100)}%`,
                            }}
                          />
                        </div>
                      </div>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="py-3 px-4">
                    {r.revparHistory.length > 1 ? (
                      <ResponsiveContainer width="100%" height={32}>
                        <AreaChart
                          data={[...r.revparHistory].reverse()}
                          margin={{ top: 2, right: 2, left: 2, bottom: 2 }}
                        >
                          <Area
                            type="monotone"
                            dataKey="revpar"
                            stroke="#3b82f6"
                            fill="#3b82f6"
                            fillOpacity={0.2}
                            strokeWidth={1.5}
                            dot={false}
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    ) : (
                      <span className="text-muted-foreground text-xs">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Tab: Settings ────────────────────────────────────────────────────────────

const settingsSchema = z.object({
  greenThresholdMin: z.coerce.number(),
  yellowThresholdMin: z.coerce.number().max(0),
  orangeThresholdMin: z.coerce.number().max(0),
  dynamicWeeklyRate: z.boolean(),
  minDaysWithoutPayment: z.coerce.number().int().min(1).max(90),
  collectionRateAlertThreshold: z.coerce.number().min(0).max(100),
  excludeInactiveRooms: z.boolean(),
});

type SettingsForm = z.infer<typeof settingsSchema>;

function SettingsTab() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery<AnalyticsSettings | null>({
    queryKey: ["/api/app/analytics/settings"],
  });

  const form = useForm<SettingsForm>({
    resolver: zodResolver(settingsSchema),
    defaultValues: {
      greenThresholdMin: 0,
      yellowThresholdMin: -299,
      orangeThresholdMin: -500,
      dynamicWeeklyRate: false,
      minDaysWithoutPayment: 14,
      collectionRateAlertThreshold: 80,
      excludeInactiveRooms: true,
    },
    values: data
      ? {
          greenThresholdMin: parseFloat(String(data.greenThresholdMin)),
          yellowThresholdMin: parseFloat(String(data.yellowThresholdMin)),
          orangeThresholdMin: parseFloat(String(data.orangeThresholdMin)),
          dynamicWeeklyRate: data.dynamicWeeklyRate,
          minDaysWithoutPayment: data.minDaysWithoutPayment,
          collectionRateAlertThreshold: parseFloat(
            String(data.collectionRateAlertThreshold),
          ),
          excludeInactiveRooms: data.excludeInactiveRooms,
        }
      : undefined,
  });

  const mutation = useMutation({
    mutationFn: (values: SettingsForm) =>
      apiRequest("PUT", "/api/app/analytics/settings", values),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/app/analytics/settings"] });
      qc.invalidateQueries({ queryKey: ["/api/app/analytics/overview"] });
      qc.invalidateQueries({ queryKey: ["/api/app/analytics/member-health"] });
      toast({ title: "Settings saved" });
    },
    onError: (err) => {
      toast({
        title: "Failed to save settings",
        description: parseErrorMessage(err, "Unknown error"),
        variant: "destructive",
      });
    },
  });

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-80" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-6">
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit((v) => mutation.mutate(v))}
          className="space-y-6"
        >
          {/* Tier thresholds */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">
                Economic Occupancy Tier Thresholds
              </CardTitle>
              <CardDescription>
                Dollar amounts a member's balance must reach to enter each tier. Use
                negative values for amounts owed.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={form.control}
                name="greenThresholdMin"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-green-500" />
                      Green minimum ($)
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        {...field}
                        data-testid="setting-green-threshold"
                      />
                    </FormControl>
                    <FormDescription>
                      Stored balance ≥ this = green tier (default: $0; no stored balance due)
                    </FormDescription>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="yellowThresholdMin"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-yellow-500" />
                      Yellow minimum ($)
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        {...field}
                        data-testid="setting-yellow-threshold"
                      />
                    </FormControl>
                    <FormDescription>
                      Balance ≥ this = Slightly behind (default: -$299)
                    </FormDescription>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="orangeThresholdMin"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-orange-500" />
                      Orange minimum ($)
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        {...field}
                        data-testid="setting-orange-threshold"
                      />
                    </FormControl>
                    <FormDescription>
                      Balance ≥ this = Moderately behind (default: -$500)
                    </FormDescription>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="dynamicWeeklyRate"
                render={({ field }) => (
                  <FormItem className="flex items-start gap-3">
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        data-testid="setting-dynamic-rate"
                      />
                    </FormControl>
                    <div>
                      <FormLabel>Dynamic Yellow boundary</FormLabel>
                      <FormDescription>
                        Yellow threshold tracks each member's weekly rate instead of a
                        fixed dollar amount.
                      </FormDescription>
                    </div>
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {/* Alert settings */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Alert Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={form.control}
                name="collectionRateAlertThreshold"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Collection rate alert threshold (%)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min={0}
                        max={100}
                        {...field}
                        data-testid="setting-collection-rate-alert"
                      />
                    </FormControl>
                    <FormDescription>
                      Alert fires when collection rate drops below this % (default: 80%)
                    </FormDescription>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="minDaysWithoutPayment"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Min. days without payment (overdue flag)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min={1}
                        max={90}
                        {...field}
                        data-testid="setting-min-days"
                      />
                    </FormControl>
                    <FormDescription>
                      Members without a payment for this many days are flagged overdue
                      (default: 14)
                    </FormDescription>
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {/* Room settings */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Room Settings</CardTitle>
            </CardHeader>
            <CardContent>
              <FormField
                control={form.control}
                name="excludeInactiveRooms"
                render={({ field }) => (
                  <FormItem className="flex items-start gap-3">
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        data-testid="setting-exclude-inactive"
                      />
                    </FormControl>
                    <div>
                      <FormLabel>Exclude inactive rooms from analytics</FormLabel>
                      <FormDescription>
                        When enabled, rooms marked inactive don't count toward RevPAR or
                        occupancy.
                      </FormDescription>
                    </div>
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <Button
            type="submit"
            disabled={mutation.isPending}
            data-testid="settings-save-button"
          >
            {mutation.isPending ? "Saving…" : "Save Settings"}
          </Button>
        </form>
      </Form>
    </div>
  );
}

// ── AI insights (Phase 3) ────────────────────────────────────────────────────

type PulseStatus = "green" | "yellow" | "red";
type AiPropertyInsight = { propertyId: number; name: string; summary: string };
type AiSummaryResponse = {
  paymentEvidence: { source: "stored_member_records"; freshness: "unverified"; tierCounts: TierCounts };
  generatedAt: string;
  source: "gemini" | "fallback" | "cache" | "unavailable";
  period: string | null;
  previousPeriod: string | null;
  pulseSignal: { status: PulseStatus; headline: string; reason: string } | null;
  healthSummaries: AiPropertyInsight[];
  varianceExplanations: AiPropertyInsight[];
};

const PULSE_TONE: Record<PulseStatus, { dot: string; ring: string; label: string }> = {
  green: {
    dot: "bg-emerald-500",
    ring: "border-emerald-200 bg-emerald-50 dark:border-emerald-900/50 dark:bg-emerald-950/30",
    label: "On track",
  },
  yellow: {
    dot: "bg-amber-500",
    ring: "border-amber-200 bg-amber-50 dark:border-amber-900/50 dark:bg-amber-950/30",
    label: "Watch",
  },
  red: {
    dot: "bg-red-500",
    ring: "border-red-200 bg-red-50 dark:border-red-900/50 dark:bg-red-950/30",
    label: "At risk",
  },
};

export function PulseSignalBanner({ signal }: { signal: NonNullable<AiSummaryResponse["pulseSignal"]> }) {
  const tone = PULSE_TONE[signal.status] ?? PULSE_TONE.yellow;
  return (
    <div
      className={`flex items-start gap-3 rounded-xl border p-4 ${tone.ring}`}
      data-testid="pulse-signal-banner"
    >
      <span className="relative mt-1 flex h-3 w-3 shrink-0">
        <span className={`absolute inline-flex h-full w-full rounded-full opacity-60 ${tone.dot}`} />
        <span className={`relative inline-flex h-3 w-3 rounded-full ${tone.dot}`} />
      </span>
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <Activity className="h-4 w-4 text-muted-foreground" />
          <p
            className="font-semibold text-gray-900 dark:text-gray-50"
            data-testid="text-pulse-headline"
          >
            {signal.headline}
          </p>
          <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {tone.label}
          </span>
        </div>
        <p className="mt-1 text-sm text-muted-foreground" data-testid="text-pulse-reason">
          {signal.reason}
        </p>
      </div>
    </div>
  );
}

export function AiInsightsPanel() {
  const { data, isLoading } = useQuery<AiSummaryResponse>({
    queryKey: ["/api/app/analytics/ai-summary"],
  });

  if (isLoading) return <LoadingPanel />;
  if (!data?.pulseSignal) {
    return <EmptyPanel message="Not enough completed earnings data for AI insights yet." />;
  }
  const properties = new Map<number, { name: string; narratives: { label: string; text: string }[] }>();
  for (const [label, entries] of [["Health", data.healthSummaries], ["Month-over-month explanation", data.varianceExplanations]] as const) {
    for (const entry of entries) {
      const property = properties.get(entry.propertyId) ?? { name: entry.name, narratives: [] };
      const text = String(entry.summary ?? "").trim();
      const normalize = (s: string) => s.replace(/\s+/g, " ").trim();
      if (text && !property.narratives.some((n) => normalize(n.text) === normalize(text))) property.narratives.push({ label, text });
      properties.set(entry.propertyId, property);
    }
  }

  return (
    <div className="space-y-6" data-testid="panel-ai-insights">
      <PulseSignalBanner signal={data.pulseSignal} />

      <div>
        <div className="mb-3 flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-brand" />
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-50">
            Property health (month over month)
          </h2>
        </div>
        {properties.size === 0 ? (
          <EmptyPanel message="No property health data yet — import income to unlock summaries." />
        ) : (
          <div className="grid gap-4 md:grid-cols-2" data-testid="ai-health-summaries">
            {Array.from(properties, ([id, h]) => (
              <Card key={id} data-testid={`card-health-summary-${id}`}>
                <CardContent className="pt-6">
                  <p className="font-semibold text-gray-900 dark:text-gray-50">{h.name}</p>
                  {h.narratives.map((n) => <div key={`${n.label}:${n.text}`} className="mt-3">
                    <p className="text-xs font-medium">{n.label}</p>
                    <p className="mt-1 text-sm text-muted-foreground">{n.text}</p>
                  </div>)}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <p className="text-xs text-muted-foreground" data-testid="text-ai-source">
        {data.source === "fallback"
          ? "Generated from your metrics."
          : data.source === "cache" ? "Cached portfolio summary." : "AI-generated from your portfolio data."}
        {" "}Payment tiers describe stored member records, not live PadSplit verification. Payment-field freshness is unverified.
        {data.paymentEvidence && ` Unknown stored balances: ${data.paymentEvidence.tierCounts.unknown}.`}
      </p>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function AppPortfolioAnalytics() {
  const meQuery = useQuery<{
    user?: { selectedPlan?: string };
    effectivePlan?: AccountType;
  }>({
    queryKey: ["/api/app/auth/me"],
    staleTime: 60_000,
  });

  const plan = (meQuery.data?.effectivePlan ||
    meQuery.data?.user?.selectedPlan ||
    "Host") as AccountType;

  // Fail closed: don't render the feature until the plan is confirmed.
  // Modular accounts pass — the analytics module gate is enforced by the
  // entitlement middleware (403 → in-context module upgrade panel).
  const analyticsIsModular =
    (meQuery.data?.user as { accountKind?: string | null } | undefined)?.accountKind != null;
  const tierAllowed = meQuery.isSuccess && (analyticsIsModular || hasFeatureAccess(plan, "Professional"));

  return (
    <AppLayout>
      {meQuery.isLoading ? (
        <div className="p-6 space-y-2" data-testid="loading-portfolio-analytics">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-3/4" />
        </div>
      ) : !tierAllowed ? (
        <div
          className="flex flex-col items-center justify-center min-h-[60vh] gap-4 text-center px-6"
          data-testid="tier-gate-portfolio-analytics"
        >
          <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-amber-50">
            <Lock className="h-8 w-8 text-amber-500" />
          </div>
          <h1 className="text-2xl font-bold">Portfolio Analytics</h1>
          <p className="text-sm text-muted-foreground max-w-md">
            Portfolio Analytics is available on the Professional plan. Upgrade to unlock
            RevPAR tracking, economic occupancy, variance analysis, and AI-driven portfolio
            health insights.
          </p>
          <Link href="/app/account/billing">
            <Button data-testid="button-upgrade-portfolio-analytics">
              Upgrade to Professional
            </Button>
          </Link>
        </div>
      ) : (
        <BetaAccessGate
          featureKey="portfolio_analytics"
          featureName="Portfolio Analytics"
          description="The Portfolio Analytics dashboard provides RevPAR tracking, economic occupancy tiers, variance analysis, and member health monitoring."
        >
          <div className="p-4 md:p-6 max-w-7xl mx-auto space-y-5">
            {/* Page header */}
            <div className="flex items-start justify-between gap-4">
              <div>
                <h1
                  className="text-2xl font-bold tracking-tight"
                  data-tour="portfolio-analytics-heading"
                >
                  Portfolio Analytics
                </h1>
                <p className="text-muted-foreground text-sm mt-0.5">
                  RevPAR · Economic occupancy · Member health · Room performance
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-xs text-muted-foreground">
                  Beta
                </Badge>
                <ExplainerVideoButton pageKey="portfolio-analytics" />
                <GuideButton pageKey="portfolio-analytics" />
              </div>
            </div>

            <GuideNudgeBanner pageKey="portfolio-analytics" />

            <Tabs defaultValue="overview" className="space-y-5">
              <TabsList
                className="flex flex-wrap h-auto gap-1 p-1"
                data-tour="portfolio-analytics-tabs"
              >
                <TabsTrigger value="overview" className="gap-1.5" data-testid="tab-overview">
                  <BarChart3 className="h-4 w-4" />
                  Overview
                </TabsTrigger>
                <TabsTrigger value="income" className="gap-1.5" data-testid="tab-income">
                  <DollarSign className="h-4 w-4" />
                  Income Analysis
                </TabsTrigger>
                <TabsTrigger value="insights" className="gap-1.5" data-testid="tab-insights">
                  <Sparkles className="h-4 w-4" />
                  AI Insights
                </TabsTrigger>
                <TabsTrigger value="members" className="gap-1.5" data-testid="tab-members">
                  <Users className="h-4 w-4" />
                  Member Health
                </TabsTrigger>
                <TabsTrigger value="rooms" className="gap-1.5" data-testid="tab-rooms">
                  <BedDouble className="h-4 w-4" />
                  Room Performance
                </TabsTrigger>
                <TabsTrigger
                  value="settings"
                  className="gap-1.5"
                  data-testid="tab-settings"
                >
                  <Settings className="h-4 w-4" />
                  Settings
                </TabsTrigger>
              </TabsList>

              <TabsContent value="insights" className="mt-0" data-tour="portfolio-analytics-insights">
                <AiInsightsPanel />
              </TabsContent>
              <TabsContent value="overview" className="mt-0">
                <OverviewTab />
              </TabsContent>
              <TabsContent value="income" className="mt-0">
                <IncomeAnalysisTab />
              </TabsContent>
              <TabsContent value="members" className="mt-0">
                <MemberHealthTab />
              </TabsContent>
              <TabsContent value="rooms" className="mt-0">
                <RoomPerformanceTab />
              </TabsContent>
              <TabsContent value="settings" className="mt-0">
                <SettingsTab />
              </TabsContent>
            </Tabs>
          </div>
        </BetaAccessGate>
      )}
    </AppLayout>
  );
}
